/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * ClientDirectMessagesTab - Coach view for direct messaging threads with a specific client
 * Shows a list of all conversation threads with the student
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MessageSquare,
  Loader2,
  AlertCircle,
  Plus,
  ChevronRight,
  Clock,
  Archive
} from 'lucide-react'
import messagingService from '../services/messagingService'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('ClientDirectMessagesTab')

const ClientDirectMessagesTab = ({ clientId, clientName, currentUserId, permissions }) => {
  const navigate = useNavigate()
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [showNewThreadForm, setShowNewThreadForm] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newThreadData, setNewThreadData] = useState({ subject: '', message: '' })

  // Check if coach has permission to send messages
  const canSendMessages = permissions?.canSendMessages !== false

  // Load all conversations with this client
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true)
      // Get all conversations and filter to those with this participant
      const response = await messagingService.getConversations({ limit: 100, status: 'active' })

      if (response.success) {
        // Filter conversations with this client
        const clientConversations =
          response.conversations?.filter((c) => c.participantId === clientId) || []
        setConversations(clientConversations)
      }
    } catch (error) {
      logger.error('Error loading conversations:', error)
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    if (clientId) {
      loadConversations()
    }
  }, [clientId, loadConversations])

  const handleStartNewThread = async (e) => {
    e.preventDefault()
    if (!newThreadData.message.trim()) {
      toast.error('Please enter a message')
      return
    }

    try {
      setCreating(true)
      const response = await messagingService.startConversation({
        coachId: currentUserId,
        participantId: clientId,
        participantType: 'student',
        subject: newThreadData.subject.trim() || null,
        initialMessage: newThreadData.message.trim()
      })

      if (response.success) {
        toast.success('Thread started')
        setNewThreadData({ subject: '', message: '' })
        setShowNewThreadForm(false)
        await loadConversations()
      }
    } catch (error) {
      logger.error('Error starting thread:', error)
      toast.error('Failed to start thread')
    } finally {
      setCreating(false)
    }
  }

  const handleOpenThread = (conversationId) => {
    // Navigate to messages page with this conversation selected
    navigate(`/messages/${conversationId}`)
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  if (!canSendMessages) {
    return (
      <div className="p-6 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
          <AlertCircle className="w-5 h-5" />
          <p className="font-medium">Direct messaging not permitted</p>
        </div>
        <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
          The student has limited your messaging permissions. Contact them to request additional
          access.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Message Threads with {clientName}
          </h3>
        </div>
        {!showNewThreadForm && (
          <button
            onClick={() => setShowNewThreadForm(true)}
            className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            New Thread
          </button>
        )}
      </div>

      {/* New Thread Form */}
      {showNewThreadForm && (
        <div className="card">
          <div className="card-body">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
              Start a new thread with {clientName}
            </h4>
            <form onSubmit={handleStartNewThread} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subject <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newThreadData.subject}
                  onChange={(e) => setNewThreadData({ ...newThreadData, subject: e.target.value })}
                  placeholder="e.g., Algebra Help, Writing Assignment"
                  className="input w-full"
                  disabled={creating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Message
                </label>
                <textarea
                  value={newThreadData.message}
                  onChange={(e) => setNewThreadData({ ...newThreadData, message: e.target.value })}
                  placeholder="Write your message..."
                  rows={3}
                  className="input w-full resize-none"
                  disabled={creating}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewThreadForm(false)
                    setNewThreadData({ subject: '', message: '' })
                  }}
                  className="btn btn-secondary"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newThreadData.message.trim()}
                  className="btn btn-primary"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      Starting...
                    </>
                  ) : (
                    'Start Thread'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Threads List */}
      {conversations.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              No message threads with {clientName} yet
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Start a new thread to begin messaging
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {conversations.map((conversation) => {
            const lastMessage = conversation.messages?.[0]
            const hasUnread = conversation.coachUnreadCount > 0

            return (
              <button
                key={conversation.id}
                onClick={() => handleOpenThread(conversation.id)}
                className={`w-full card hover:shadow-md transition-shadow ${
                  hasUnread ? 'border-l-4 border-l-primary-500' : ''
                }`}
              >
                <div className="card-body py-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <h4
                          className={`font-medium truncate ${
                            hasUnread
                              ? 'text-gray-900 dark:text-gray-100'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {conversation.subject || 'No subject'}
                        </h4>
                        {hasUnread && (
                          <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500" />
                        )}
                        {conversation.status === 'archived' && (
                          <Archive className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </div>
                      {lastMessage && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {lastMessage.senderId === currentUserId ? 'You: ' : ''}
                          {lastMessage.content}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {lastMessage && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(lastMessage.createdAt)}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ClientDirectMessagesTab
